<template>
  <div class="app">

    <template v-if="logoutRoute || loginRoute">
      <main id="main" class="auth-transition">
        <router-view :key="route.path" />
      </main>
    </template>
    <template v-else-if="authenticated && agreementRoute">
      <main id="main" class="auth-transition">
        <router-view :key="route.path" />
      </main>
    </template>
    <template v-else-if="authenticated && !publicProjectRoute">
      <NavMenuPanel />
      <NavigationGlobal class="global-nav is-hidden-mobile" :dark="menuIsDark"/>
      <NavigationContextual class="context-nav is-hidden-mobile" :dark="menuIsDark"/>
      <main id="main" class="main" :class="{'is-scroll-locked': mobileNavOpen}">
        <NavigationMobileHeader
          class="is-hidden-tablet"
          @update:open="mobileNavOpen = $event"
        />
        <div class="main-content">
          <router-view :key="route.path" />
        </div>
        <footer class="app-footer">
          <aside v-if="projectShowRoute" class="app-footer__support" aria-label="Project support information">
            <p><strong>Project questions?</strong> Contact your contractor or use the <strong>Submit Question</strong> tab.</p>
            <p><strong>Technical difficulties?</strong> Email <a href="mailto:support@preconsuite.com">support@preconsuite.com</a> or call <a href="tel:+19492220400">949-222-0400</a>.</p>
          </aside>
          <div class="app-footer__links">
            <a class="is-logo" href="https://pipelinesuite.com/" target="_blank"><img src="https://assets.p-suite.io/powered_by_preconSuiteLogo.svg" width="200" alt="Powered by PreconSuite"></a>
            <a class="is-link" href="https://pipelinesuite.com/privacy-policy" target="_blank">Privacy</a>
            <a class="is-link" href="https://pipelinesuite.com/terms-of-service" target="_blank">Terms</a>
            <a class="is-link" href="https://pipelinesuite.com/contact/" target="_blank">Contact Us</a>
          </div>
        </footer>
      </main>

      <VerifyHumanModal @verified="handleEmailResponse" v-if="isVerifyHumanActive" />
    </template>
    <template v-else-if="publicProjectRoute">
      <OpenNavigationGlobal class="is-hidden-mobile" :dark="menuIsDark" />
      <div id="main" class="public-main">
        <Header />
        <div class="main-content">
          <router-view :key="route.path" />
        </div>
        <footer class="app-footer">
          <div class="app-footer__links">
            <a class="is-logo" href="https://pipelinesuite.com/" target="_blank"><img src="https://assets.p-suite.io/powered_by_preconSuiteLogo.svg" width="200" alt="Powered by PreconSuite"></a>
            <a class="is-link" href="https://pipelinesuite.com/privacy-policy" target="_blank">Privacy</a>
            <a class="is-link" href="https://pipelinesuite.com/terms-of-service" target="_blank">Terms</a>
            <a class="is-link" href="https://pipelinesuite.com/contact/" target="_blank">Contact Us</a>
          </div>
        </footer>
      </div>
    </template>
    <template v-else>
      <main id="main" class="auth-transition">
        <template v-if="callbackRoute">
          <router-view :key="route.path" />
        </template>
        <template v-else>
          <div class="auth-loading" aria-live="polite">
            <LoadingIndicator message="Redirecting to sign in…" />
          </div>

          <!-- Keep login route hooks mounted while the browser leaves this app. -->
          <div class="auth-route" aria-hidden="true">
            <router-view :key="route.path" />
          </div>
        </template>
      </main>
    </template>

  </div>
</template>

<script setup lang="ts">

  import NavMenuPanel from './components/NavMenuPanel.vue'
  import NavigationGlobal from './components/NavigationGlobal.vue'
  import NavigationContextual from './components/NavigationContextual.vue'
  import NavigationMobileHeader from "./components/NavigationMobileHeader.vue";
  import VerifyHumanModal from "@/components/VerifyHumanModal.vue";

  import {computed, onMounted, ref, watch} from "vue";
  import { useRoute, useRouter } from "vue-router";
  import {storeToRefs} from "pinia";
  import {useCompanyStore} from "@/stores/CompanyStore";

  import Auth from "@/auth";
  import Header from "@/components/Header.vue";
  import LoadingIndicator from '@/components/LoadingIndicator.vue';
  import OpenNavigationGlobal from "@/components/PublicNavigationGlobal.vue";
  import BidDialog from "@/use/BidDialog";
  import {NotificationError, NotificationSuccess} from "@/internal";
  import {useBidPackageProjectBidderStore} from "@/stores/BidPackageProjectBidderStore";
  import {useProjectStore} from "@/stores/ProjectStore";
  import {useClientStore} from "@/stores/ClientStore";
  import useClientTheme from '@/use/clientTheme';
  import {clientSubdomainMatches} from '@/use/clientSubdomain';
  const bidPackageProjectBidderStore = useBidPackageProjectBidderStore();
  const projectStore = useProjectStore();
  const companyStore = useCompanyStore();
  const {company} = storeToRefs(companyStore);

  const route = useRoute();
  const router = useRouter();
  const mobileNavOpen = ref(false);
  const isVerifyHumanActive = ref<boolean>(false);
  const companyRefreshComplete = ref(false);
  const pendingEmailResponseKey = 'pending_email_response';

  type PendingEmailResponse = {
    confirmed: string;
    project_id: number;
    company_id: number;
  };

  const authenticated = computed(() => {
    return Auth.isAuthenticated();
  });

  const publicProjectRoute = computed(() => route && route.name?.startsWith('open-projects/'));
  const callbackRoute = computed(() => route.name === 'callback');
  const loginRoute = computed(() => route.name === 'login');
  const logoutRoute = computed(() => route.name === 'logout');
  const termsRoute = computed(() => route.name === 'terms');
  const ndaRoute = computed(() => route.name === 'nda');
  const agreementRoute = computed(() => termsRoute.value || ndaRoute.value);
  const projectShowRoute = computed(() => [
    'projects/show',
    'projects/bid-packages/show',
    'projects/past/show',
    'projects/past/bid-packages/show',
  ].includes(String(route.name)));

  const isTermsActive = computed(() => companyRefreshComplete.value && authenticated.value && !company.value?.latest_terms_accepted);

  const clientStore = useClientStore();
  const {client, menuIsDark} = storeToRefs(clientStore);

  const clientLogoURL = computed<string | null>(() => client.value?.logo_url || null);
  const clientBodyBackgroundColor = computed<string|null>(() => client.value?.sp_body_background ? `#${client.value.sp_body_background}` : 'FFFCEE');
  const clientBorderColor = computed<string|null>(() => client.value?.sp_border_color ? `#${client.value.sp_border_color}` : 'F57F0C');
  const clientMenuBackgroundColor = computed<string|null>(() => client.value?.sp_menu_background ? `#${client.value.sp_menu_background}` : 'FB860C');
  const clientMenuTextColor = computed<string|null>(() => client.value?.sp_menu_color ? `#${client.value.sp_menu_color}` : 'FFFFFF');

  const {setRootThemeVariables} = useClientTheme({
    clientLogoURL,
    clientBodyBackgroundColor,
    clientBorderColor,
    clientMenuBackgroundColor,
    clientMenuTextColor,
  });
  onMounted(async () => {
    checkForEmailResponse();

    if (authenticated.value && !callbackRoute.value && !logoutRoute.value) {
      const companyMatchesTenant = await refreshCompany();
      if (!companyMatchesTenant) {
        return;
      }
    } else {
      companyRefreshComplete.value = true;
    }

    redirectToTermsIfRequired();

    if (authenticated.value || publicProjectRoute.value) {
      BidDialog();
    }
  });

  async function refreshCompany(): Promise<boolean> {
    try {
      await companyStore.getCompany({
        params: {
          include: [
            'client.latestTerms',
            'primaryContact',
            'trades',
          ],
        },
      });

      const authenticatedClient = company.value?.client;
      if (!authenticatedClient || !clientSubdomainMatches(authenticatedClient.subdomain)) {
        companyStore.clear();
        Auth.logout(undefined, window.location.href);
        return false;
      }

      clientStore.setBranding(authenticatedClient);

      return true;
    } catch (error: any) {
      companyStore.clear();
      NotificationError({ message: 'We could not verify your access. Please sign in again.' });
      return false;
    } finally {
      companyRefreshComplete.value = true;
    }
  }

  function checkForEmailResponse() {
    const pendingResponse = getPendingEmailResponse();

    if (!authenticated.value || isTermsActive.value || !pendingResponse || !company.value?.latest_terms_accepted) {
      isVerifyHumanActive.value = false;
      return;
    }

    if (!['projects/show', 'projects/bid-packages/show'].includes(String(route.name))) {
      isVerifyHumanActive.value = false;
      return;
    }

    const routeProjectId = Number(route.params?.projectId);
    if (routeProjectId !== pendingResponse.project_id || Number(company.value.id) !== pendingResponse.company_id) {
      window.sessionStorage.removeItem(pendingEmailResponseKey);
      isVerifyHumanActive.value = false;
      return;
    }

    isVerifyHumanActive.value = true;
  }

  function getPendingEmailResponse(): PendingEmailResponse | null {
    const storedResponse = window.sessionStorage.getItem(pendingEmailResponseKey);
    if (!storedResponse) {
      return null;
    }

    let parsed;
    try {
      parsed = JSON.parse(storedResponse);
    } catch {
      window.sessionStorage.removeItem(pendingEmailResponseKey);
      return null;
    }

    if (typeof parsed?.confirmed !== 'string'
      || !Number.isInteger(Number(parsed?.project_id))
      || !Number.isInteger(Number(parsed?.company_id))) {
      window.sessionStorage.removeItem(pendingEmailResponseKey);
      return null;
    }

    return {
      confirmed: parsed.confirmed,
      project_id: Number(parsed.project_id),
      company_id: Number(parsed.company_id),
    };
  }

  function redirectToTermsIfRequired() {
    if (!isTermsActive.value || termsRoute.value) {
      return;
    }

    window.sessionStorage.setItem('redirectPath', window.location.href);
    router.replace({ name: 'terms' });
  }

  async function handleEmailResponse() {
    const pendingResponse = getPendingEmailResponse();

    if (!pendingResponse
      || !['projects/show', 'projects/bid-packages/show'].includes(String(route.name))
      || Number(route.params?.projectId) !== pendingResponse.project_id
      || Number(company.value?.id) !== pendingResponse.company_id) {
      return;
    }

    try {
      await bidPackageProjectBidderStore.emailResponse(pendingResponse.project_id, {
        confirmed: pendingResponse.confirmed,
      });

      await projectStore.show(pendingResponse.project_id, {
        params: {
          include: [
            'bidders.trade',
            'bidPackages',
            'estimators',
            'notices',
            'projectContacts',
          ],
        },
      });

      window.sessionStorage.removeItem(pendingEmailResponseKey);
      isVerifyHumanActive.value = false;
      NotificationSuccess({ message: 'Your response has been recorded.' });
    } catch (error) {
      isVerifyHumanActive.value = false;
      NotificationError({
        message: 'We could not save your response. Please try again.',
      });
    }
  }

  watch(company, () => {
    checkForEmailResponse();
    redirectToTermsIfRequired();
  }, {deep: true});

  watch(client, setRootThemeVariables, {immediate: true, deep: true});

  watch(route, () => {
    checkForEmailResponse();
    redirectToTermsIfRequired();
  }, {deep: true});

</script>

<style lang="scss" scoped>

.app {
  display: flex;
  width: 100%;
  height: 100%;

  .main-content {
    flex: 1 0 auto;
    min-width: 0;
  }

  .main.is-scroll-locked {
    overflow: hidden;
  }

  .public-main {
    display: flex;
    flex: 1;
    flex-direction: column;
    height: 100%;
    min-width: 0;
    overflow-y: auto;
  }

  .auth-transition {
    display: flex;
    flex: 1;

    .auth-route {
      display: none;
    }
  }

  .auth-loading {
    align-items: center;
    display: flex;
    flex: 1;
    justify-content: center;
  }

  .app-footer {
    align-items: center;
    display: flex;
    flex: 0 0 auto;
    gap: 1rem 2rem;
    justify-content: space-between;
    margin-top: 1em;
    padding: 15px 45px;
    width: 100%;

    &__support {
      color: $grey-dark;
      font-size: .75rem;
      line-height: 1.4;
      max-width: 42rem;

      p { margin: 0; }
      p + p { margin-top: .2rem; }
    }

    &__links {
      align-items: center;
      display: flex;
      flex: 0 0 auto;
      flex-wrap: wrap;
      gap: .5rem 1.5rem;
      justify-content: flex-end;
      margin-left: auto;
    }
  }
}

.logo {
  height: 6em;
  padding: 1.5em;
  will-change: filter;
  transition: filter 300ms;
}
.logo:hover {
  filter: drop-shadow(0 0 2em #646cffaa);
}
.logo.vue:hover {
  filter: drop-shadow(0 0 2em #42b883aa);

}

@media (max-width: 768px) {
  .main {
    padding: 0 !important;
    width: 100%;
    min-width: 0;
  }
  .app {
    .app-footer {
      padding: 15px 15px;
      align-items: flex-start;
      flex-direction: column;
      .is-logo{
        flex-basis: 100%;
        text-align: center;
      }
      .is-link{
        flex:auto;
        text-align:center;
      }
      &__links {
        justify-content: flex-start;
      }
    }
  }
}
</style>
